import { AxiosError, type AxiosResponse } from "axios";

/**
 * A save the server refused must never look like a save that landed.
 *
 * feed-processor's save endpoints refuse with a 400 whose body is
 * `{ detail }` (see `edit_algo_function` there), and axios throws on any
 * non-2xx, so the editor's mutation errors, `ErrorBanner` renders `detail`,
 * and `onSuccess` -- which navigates to the feed -- never runs. That is the
 * contract.
 *
 * This is the guard for the other shape: a 2xx whose body carries an `error`
 * key. Older builds of the API returned refusals that way -- FastAPI sends a
 * bare `return {"error": ...}` as a 200 -- and prod runs overlay images, so
 * the UI cannot assume every server it talks to is current. The save clients
 * used to ignore a 2xx body entirely: `editFeed` invalidated its caches and
 * the route navigated to the view page, so the author saw a successful save
 * that had not happened. Throwing here turns that body into the same
 * `AxiosError` a real 400 produces, so the banner shows the message and the
 * page stays put.
 *
 * No path-alias imports on purpose: `scripts/verify-save-refusal.mjs` runs
 * this under bare node with types stripped.
 */
export function refusalMessage(data: unknown): string | null {
  if (!data || typeof data !== "object" || !("error" in data)) return null;
  const error = (data as { error: unknown }).error;
  if (error === null || error === undefined || error === false) return null;
  return typeof error === "string" ? error : JSON.stringify(error);
}

/** Return the body of an accepted save, or throw the refusal it carries. */
export function assertSaveAccepted<T>(response: AxiosResponse<T>): T {
  const message = refusalMessage(response.data);
  if (message !== null) {
    throw new AxiosError(
      message,
      AxiosError.ERR_BAD_RESPONSE,
      response.config,
      response.request,
      response,
    );
  }
  return response.data;
}
